import { FC, useCallback, useContext, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Button, H2, Paragraph } from '@smartshell/ui-kit';
import { SmartCaptcha } from '@yandex/smart-captcha';

import { LocalizationContext } from 'localization';
import { START_VERIFICATION } from 'schemas';
import { SettingsContext } from 'settings';
import {
  EVerificationType,
  IStartVerificationRequest,
  IStartVerificationResponse,
} from 'types';

import { IChangeVerifyMethodProps } from './interfases';
import s from './Verify.module.scss';

export const ChangeVerifyMethod: FC<IChangeVerifyMethodProps> = ({
  setIsChangeMethod,
  phone,
  setVerificationType,
  setNotValidMethod,
  notValidMethod,
  email,
  setVerification,
}) => {
  const {
    state: {
      settings: { countryCode },
    },
  } = useContext(SettingsContext);

  const { t, lang } = useContext(LocalizationContext);
  const [errors, setErrors] = useState<Set<EVerificationType>>(new Set());
  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const [startVerification] = useMutation<
    IStartVerificationResponse,
    IStartVerificationRequest
  >(START_VERIFICATION);

  const startVerificationHandler = useCallback(
    (type: EVerificationType) => {
      if (!captchaToken) return;

      startVerification({
        variables: {
          input: {
            phone,
            type,
            captcha_token: captchaToken,
            email,
          },
        },
      })
        .then((data) => {
          if (!data.data?.startVerification) return;

          if (!data.data.startVerification.success) {
            throw new Error('Ошибка');
          }

          setIsChangeMethod(false);
          setVerificationType(type);
          setVerification?.({ ...data.data.startVerification });
        })
        .catch(() => {
          setCaptchaKey((prev) => prev + 1);
          setNotValidMethod(type);
          setErrors((prev) => {
            const newSet = new Set(prev);
            newSet.add(type);
            return newSet;
          });
        });
    },
    [setVerificationType, captchaToken]
  );

  return (
    <div className={s.changeVerifyMethod}>
      <H2>{t('clientRegister.change_method.title', phone)}</H2>
      <div className={s.changeVerifyMethod__methods}>
        {countryCode === 'RU' ? (
          <div className={s.changeVerifyMethod__method}>
            <Button
              variant="secondary"
              fullWidth
              iconName="IncomingCallIcon"
              disabled={
                notValidMethod.has(EVerificationType.INCOMING_CALL) ||
                errors.has(EVerificationType.INCOMING_CALL) ||
                !captchaToken
              }
              onClick={() =>
                startVerificationHandler(EVerificationType.INCOMING_CALL)
              }
            >
              {t('clientRegister.method.incoming_call')}
            </Button>
            {errors.has(EVerificationType.INCOMING_CALL) && (
              <Paragraph size="s" className={s.error}>
                {t('clientRegister.phone.not_found')}
              </Paragraph>
            )}
          </div>
        ) : (
          <div className={s.changeVerifyMethod__method}>
            <Button
              variant="secondary"
              fullWidth
              iconName="MailIcon"
              disabled={
                notValidMethod.has(EVerificationType.EMAIL) ||
                errors.has(EVerificationType.EMAIL) ||
                !captchaToken
              }
              onClick={() => startVerificationHandler(EVerificationType.EMAIL)}
            >
              {t('email')}
            </Button>
            {errors.has(EVerificationType.EMAIL) && (
              <Paragraph size="s" className={s.error}>
                {t('clientRegister.mail.not_found')}
              </Paragraph>
            )}
          </div>
        )}
        <div className={s.changeVerifyMethod__method}>
          <Button
            variant="secondary"
            fullWidth
            iconName="TelegramSocialIcon"
            disabled={
              notValidMethod.has(EVerificationType.TELEGRAM) ||
              errors.has(EVerificationType.TELEGRAM) ||
              !captchaToken
            }
            onClick={() => startVerificationHandler(EVerificationType.TELEGRAM)}
          >
            Telegram
          </Button>
          {errors.has(EVerificationType.TELEGRAM) && (
            <Paragraph size="s" className={s.error}>
              {t('clientRegister.phone.not_found')}
            </Paragraph>
          )}
        </div>
        <div className={s.changeVerifyMethod__method}>
          <Button
            variant="secondary"
            fullWidth
            iconName="VKSocialIcon"
            disabled={
              notValidMethod.has(EVerificationType.VK) ||
              errors.has(EVerificationType.VK) ||
              !captchaToken
            }
            onClick={() => startVerificationHandler(EVerificationType.VK)}
          >
            {t('clientRegister.method.vk')}
          </Button>
          {errors.has(EVerificationType.VK) && (
            <Paragraph size="s" className={s.error}>
              {t('clientRegister.phone.not_found')}
            </Paragraph>
          )}
        </div>
      </div>
      {process.env.REACT_APP_CAPTCHA_KEY && (
        <div className={s.capcha}>
          <SmartCaptcha
            key={captchaKey}
            sitekey={process.env.REACT_APP_CAPTCHA_KEY}
            language={lang === 'ru' ? 'ru' : 'en'}
            onSuccess={setCaptchaToken}
            onTokenExpired={() => setCaptchaToken(null)}
          />
        </div>
      )}
      <Button
        variant="ghost"
        fullWidth
        onClick={() => setIsChangeMethod(false)}
      >
        {t('back')}
      </Button>
    </div>
  );
};
